// @ts-strict-ignore
import { CardsInputBlock } from '@coast/schemas';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { LinkedCard } from '@shared/components/blocks/lib/cards';
import { useOpenLinkedCard } from '@shared/components/blocks/lib/cards/hooks';
import { NeedsCardFilter } from '@shared/components/blocks/lib/inputCards/sheets/NeedsCardFilter';
import { SelectCards } from '@shared/components/blocks/lib/inputCards/sheets/select';
import { Fields } from '@shared/components/blocks/types';
import { MBBottomSheet, MBBottomSheetTitleBar } from '@shared/components/bottomSheet';
import { MBLoadingIndicator } from '@shared/components/loading';
import { FIXED_WIDTH } from '@shared/components/multiSelect';
import { RelatedCardComponent } from '@shared/scenes/workflows/config';
import { hasRelatedCardFilter } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { Route, useNavStack } from '@shared/util/navigation';
import { ViewAllRelatedCards } from './ViewAllRelatedCards';

export type RouteName = 'loading' | 'needsCardFilter' | 'select' | 'view';

function getInitialRoutes(
  relatedChannelIds: number[],
  relatedCardComponent: RelatedCardComponent,
  workflowTemplateLoading: boolean,
): Route<RouteName>[] {
  if (relatedChannelIds === null || workflowTemplateLoading) {
    return [{ name: 'loading' }];
  }
  return [
    {
      name:
        relatedChannelIds.length === 0 && !hasRelatedCardFilter(relatedCardComponent)
          ? 'needsCardFilter'
          : 'select',
    },
  ];
}

interface Props {
  block: CardsInputBlock;
  values: LinkedCard[];
  fieldValues: Fields;
  onChange(values: LinkedCard[]): void;
  onRemoveCard?(card: LinkedCard): void;
  onClose(): void;
  anchorName: string;
  max?: number;
  targetChannelId?: number;
  initialRoute?: Route<RouteName>[];
  relatedChannelIds?: number[];
  relatedCardComponent?: RelatedCardComponent;
}

export function MBInputCardsBottomSheet({
  block,
  values,
  fieldValues,
  max,
  anchorName,
  onChange,
  onRemoveCard,
  onClose,
  initialRoute,
  relatedChannelIds,
  relatedCardComponent,
}: Props) {
  const channel = useContext(CardChannelContext);

  const nav = useNavStack<RouteName>(
    initialRoute || getInitialRoutes(relatedChannelIds, relatedCardComponent, false),
  );

  useEffect(() => {
    // transition from the initial unknown loading route to the one of the two possible initial routes
    if (nav.currentRoute.name === 'loading' && relatedChannelIds !== null) {
      nav.reset(getInitialRoutes(relatedChannelIds, relatedCardComponent, false));
    }
  }, [nav, relatedCardComponent, relatedChannelIds]);

  const openLinkedCard = useOpenLinkedCard();

  const renderTitleBar = useCallback(() => {
    switch (nav.currentRoute.name) {
      case 'loading':
      case 'select':
      case 'view':
        return (
          <MBBottomSheetTitleBar title={block.label} rightIcon="exit" onRightIconPress={onClose} />
        );
      case 'needsCardFilter':
        return (
          <MBBottomSheetTitleBar
            title={i18n('card.blocks.cardsInput.channelSearch', { name: block.label })}
            rightIcon="exit"
            onRightIconPress={onClose}
          />
        );
      default:
        return null;
    }
  }, [block, nav, onClose]);

  const content = useMemo<ReactNode>(() => {
    switch (nav.currentRoute.name) {
      case 'loading':
        return (
          <View padding={4} center>
            <MBLoadingIndicator />
          </View>
        );
      case 'needsCardFilter':
        return <NeedsCardFilter />;
      case 'select':
        return (
          <SelectCards
            values={values}
            fieldValues={fieldValues}
            fromChannelId={relatedChannelIds?.[0]}
            relatedCardComponent={relatedCardComponent}
            onChange={onChange}
            max={max}
          />
        );
      case 'view':
        return (
          <ViewAllRelatedCards
            cards={values}
            block={block}
            onPressCard={card => {
              onClose();
              openLinkedCard(card);
            }}
            onEditCards={() => {
              nav.push({ name: 'select' });
            }}
            onRemoveCard={onRemoveCard}
          />
        );
      default:
        return null;
    }
  }, [
    nav,
    block,
    values,
    relatedChannelIds,
    relatedCardComponent,
    onChange,
    max,
    onRemoveCard,
    onClose,
    openLinkedCard,
    fieldValues,
  ]);

  const fixedWidth = useIsDesktop() ? FIXED_WIDTH : null;

  return (
    <MBBottomSheet
      anchorName={anchorName}
      style={{ minWidth: fixedWidth?.width, minHeight: grid(48) }}
      renderTitleBar={renderTitleBar}
      onClose={onClose}
    >
      <MBView style={fixedWidth} fill>
        {/* Because of how MBBottomSheet works, we need to re-apply the context here */}
        <CardChannelContext.Provider value={channel}>{content}</CardChannelContext.Provider>
      </MBView>
    </MBBottomSheet>
  );
}
